import { useState } from "react";
import { Search, ChevronLeft } from "lucide-react";
import { GeoLocation } from "src/types/common";
import ToolbarButton from "../../SearchListings/ToolbarButton";
import SearchBox from "src/components/core/SearchBox";
import { useAddressSearchBox, AddressSelectedData } from "src/hooks/useAddressSearchBox";

interface ExpandableAddressSearchProps {
  onAddressSelected: (data: AddressSelectedData) => void;
  onLocationChange: (location: GeoLocation) => void;
  onAddressCleared?: () => void;
  isCondensed?: boolean;
}

export const ExpandableAddressSearch = ({
  onAddressSelected,
  onLocationChange,
  onAddressCleared,
  isCondensed = false,
}: ExpandableAddressSearchProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [selectedAddressData, setSelectedAddressData] = useState<AddressSelectedData | null>(null);

  const {
    addresses,
    selectedAddress,
    searchTerm,
    setSearchTerm,
    isSearching,
    handleAddressSelected: baseHandleAddressSelected,
  } = useAddressSearchBox({
    onAddressSelected: (data) => {
      setSelectedAddressData(data);
      onAddressSelected(data);
    },
    onLocationChange,
    onAddressCleared: () => {
      setSelectedAddressData(null);
      onAddressCleared?.();
    },
  });

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isExpanded) {
    return (
      <ToolbarButton
        onClick={handleToggleExpand}
        icon={<Search className="h-4 w-4" />}
        title={selectedAddressData ? `Selected: ${selectedAddressData.formattedAddress}` : "Search Map"}
        className="bg-white"
      />
    );
  }

  return (
    <div className={`${isCondensed ? '' : 'bg-white rounded-md border border-input shadow-sm p-2'}`}>
      <div className="flex items-center gap-2">
        <div
          className="transition-all duration-200 ease-in-out"
          style={{ width: isInputFocused ? '350px' : '170px' }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        >
          <SearchBox
            className="w-full"
            buttonClassName={`w-full ${!isInputFocused ? 'text-ellipsis overflow-hidden' : ''}`}
            list={addresses}
            shouldFilter={false}
            placeholder="Search Map"
            value={selectedAddress}
            onSelect={baseHandleAddressSelected}
            inputValue={searchTerm}
            setInputValue={setSearchTerm}
            isLoading={isSearching}
          />
        </div>
        <button
          onClick={handleToggleExpand}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded transition-colors"
          title="Minimize search"
        >
          <ChevronLeft className="h-4 w-4 text-gray-primary" />
        </button>
      </div>
    </div>
  );
};
